/**
 * Singing-room API client.
 *
 * Queue and turn state travel over the room's existing socket (see
 * `utils/singingRoom.js` on the server). Only two things need HTTP: fetching a
 * song's reference contour, and submitting a score — the latter because it has to
 * be authenticated, and the socket carries no verified identity.
 *
 * Nothing here touches /profile/getResult.
 */
import { getToken } from './karaoke.service';

const API = process.env.REACT_APP_API_END_POINT || 'https://gsapi.geetsuhane.com';

/** Contours are immutable once a song ships, so one fetch per song per page load. */
const contourCache = new Map();

async function asJson(response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || `Request failed (${response.status})`);
  return body;
}

/**
 * Fetch a song's reference pitch contour.
 *
 * Returns null when the song has none — that is a normal state, not a failure. A
 * song without a contour can still be sung in the room; it just cannot be scored,
 * and the UI says so rather than showing a fabricated number.
 */
export async function getContour(songId) {
  if (contourCache.has(songId)) return contourCache.get(songId);
  const promise = fetch(`${API}/karaoke/songs/${songId}/contour`)
    .then(async (response) => {
      if (response.status === 404) return null;
      return asJson(response);
    })
    .catch((e) => {
      console.error('getContour:', e.message);
      return null;
    });
  contourCache.set(songId, promise);
  return promise;
}

/**
 * Submit a finished performance.
 *
 * `detail` carries the duration and voiced ratio the client measured; the server
 * uses them to reject implausible submissions. Sending them is not optional — a
 * submission without them is treated as unscored.
 */
export async function submitScore({ songId, score, grade, detail }) {
  const token = await getToken();
  const body = await fetch(`${API}/karaoke/room/score`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ songId, score, grade, detail }),
  }).then(asJson);
  return body;
}

/** Whole stage state, for a client that connected mid-session. */
export async function getRoomState() {
  return fetch(`${API}/karaoke/room/state`).then(asJson).catch(() => null);
}
